import { api, apiList, uploadFormData } from '../../api/client';
import type {
  ChatDto,
  DisputeDto,
  FileAssetDto,
  MessageDto,
  NotificationDto,
  OfferDto,
  ReviewDto,
  TransportDto,
  TransportRequestDto,
} from '../carrierCabinet/api';

export type {
  ChatDto,
  DisputeDto,
  FileAssetDto,
  MessageDto,
  NotificationDto,
  OfferDto,
  ReviewDto,
  TransportDto,
  TransportRequestDto,
};

export interface DealDto {
  id: number;
  request: number;
  offer: number;
  customer: number;
  carrier: number;
  status: string;
  payment_status: string;
  created_at: string;
}

export interface CustomerProfileReviewDto {
  id: number;
  author_name: string;
  rating: number;
  comment: string;
  route: { from_city: string; to_city: string };
  photos: FileAssetDto[];
  created_at: string;
}

export interface CustomerProfileDto {
  id: number;
  email?: string;
  phone?: string;
  phone_verified_at?: string | null;
  password_changed_at?: string | null;
  display_name: string;
  customer_type: 'person' | 'company';
  first_name?: string;
  last_name?: string;
  company_name?: string;
  contact_name?: string;
  city: string;
  rating: string;
  completed_requests_count: number;
  push_enabled?: boolean;
  sms_active_transport?: boolean;
  email_marketing?: boolean;
  notification_preferences?: Record<string, Record<'push' | 'email' | 'sms', boolean>>;
  language?: string;
  region?: string;
  timezone?: string;
  display_currency?: 'RUB' | 'EUR';
  created_at: string;
  reviews: CustomerProfileReviewDto[];
  total_requests_count: number;
  confirmed_requests_percent: number;
  payment_on_time_percent: number;
}

export interface AcceptOfferResultDto {
  deal_id: number;
  transport_id: number;
  chat_id: number;
  invoice_id: number;
  deal_status: string;
}

export interface PlatformFeedbackDto {
  id: number;
  source_flow: string;
  usability_rating: number;
  liked_options: string[];
  improvement_text: string;
  nps_intent: string;
  created_at: string;
}

import { optimizeImageForUpload } from '../../utils/imageCompressor';

async function upload(file: File, purpose: 'checkpoint_media' | 'dispute_evidence' | 'review_photo', visibility: 'deal' | 'public') {
  const optimized = await optimizeImageForUpload(file);
  const body = new FormData();
  body.append('file', optimized);
  body.append('purpose', purpose);
  body.append('visibility', visibility);
  return api<FileAssetDto>('/api/media/', { method: 'POST', body });
}

export const customerCabinetApi = {
  acceptOffer: (offerId: number) => api<AcceptOfferResultDto>(`/api/offers/${offerId}/accept/`, { method: 'POST' }),
  startOfferChat: (offerId: number) => api<ChatDto>(`/api/offers/${offerId}/chat/`, { method: 'POST' }),
  agreeDirectPayment: (dealId: number) => api<DealDto>(`/api/deals/${dealId}/payment/agree-direct/`, { method: 'POST' }),
  answerQuestion: (requestId: number, questionId: number, answer: string) => api(`/api/requests/${requestId}/answers/`, {
    method: 'POST',
    body: { question_id: questionId, answer },
  }),
  changePassword: (currentPassword: string, newPassword: string) => api('/api/auth/password/change/', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  }),
  chats: () => apiList<ChatDto>('/api/chats/'),
  createDispute: (payload: {
    deal: number;
    opened_by_role: 'customer';
    reason: string;
    description: string;
    expected_resolution: string;
    evidence?: number[];
  }) => api<DisputeDto>('/api/disputes/', { method: 'POST', body: payload }),
  createPlatformFeedback: (payload: {
    source_flow: string;
    usability_rating: number;
    liked_options: string[];
    improvement_text: string;
    nps_intent: string;
  }) => api<PlatformFeedbackDto>('/api/reviews/platform-feedback/', { method: 'POST', body: payload }),
  createReview: (payload: {
    transport: number;
    author_role: 'customer';
    rating_overall: number;
    criteria_ratings: Record<string, number>;
    comment: string;
    attached_photos?: number[];
  }) => api<ReviewDto>('/api/reviews/', { method: 'POST', body: payload }),
  customerPaid: (dealId: number) => api<DealDto>(`/api/deals/${dealId}/payment/customer-paid/`, { method: 'POST' }),
  confirmReceipt: (transportId: number) => api<TransportDto>(`/api/deals/transports/${transportId}/receive/`, { method: 'POST' }),
  deals: () => apiList<DealDto>('/api/deals/'),
  disputes: () => apiList<DisputeDto>('/api/disputes/'),
  markAllNotificationsRead: () => api<{ updated: number }>('/api/notifications/read-all/', { method: 'POST' }),
  markNotificationRead: (id: number) => api<NotificationDto>(`/api/notifications/${id}/read/`, { method: 'POST' }),
  messages: (chatId: number) => apiList<MessageDto>(`/api/chats/${chatId}/messages/`),
  messagesPage: (chatId: number, before?: number) => api<{ results: MessageDto[]; next_before: number | null }>(
    `/api/chats/${chatId}/messages/?limit=50${before ? `&before=${before}` : ''}`,
  ),
  markChatRead: (chatId: number, lastMessageId: number) => api<{
    chat_id: number;
    last_read_message_id: number;
    read_at: string;
    unread_count: number;
    total_unread_count: number;
  }>(`/api/chats/${chatId}/read/`, { method: 'POST', body: { last_message_id: lastMessageId } }),
  myRequests: () => apiList<TransportRequestDto>('/api/requests/mine/'),
  notifications: () => apiList<NotificationDto>('/api/notifications/'),
  offers: () => apiList<OfferDto>('/api/offers/'),
  profile: () => api<CustomerProfileDto>('/api/profiles/me/customer/'),
  publicProfile: (id: number) => api<CustomerProfileDto>(`/api/profiles/customers/${id}/public/`),
  request: (id: number) => api<TransportRequestDto>(`/api/requests/${id}/`),
  updateRequest: (id: number, changes: Partial<TransportRequestDto>) => api<TransportRequestDto>(`/api/requests/${id}/edit/`, {
    method: 'PATCH',
    body: changes,
  }),
  cancelRequest: (id: number) => api<TransportRequestDto>(`/api/requests/${id}/cancel/`, { method: 'POST' }),
  reviews: async () => {
    const reviews: ReviewDto[] = [];
    for (let page = 1; ; page += 1) {
      const response = await api<ReviewDto[] | { results: ReviewDto[]; next: string | null }>(
        page === 1 ? '/api/reviews/' : `/api/reviews/?page=${page}`,
      );
      if (Array.isArray(response)) return [...reviews, ...response];
      reviews.push(...response.results);
      if (!response.next) return reviews;
    }
  },
  sendEmailVerification: () => api<{ sent: boolean; already_verified?: boolean; email?: string }>('/api/auth/email/send/', {
    method: 'POST',
  }),
  sendMessage: (chatId: number, body: string, media?: FileAssetDto) => api<MessageDto>(`/api/chats/${chatId}/messages/`, {
    method: 'POST',
    body: { body, message_type: media ? 'photo' : 'text', media: media?.id ?? null },
  }),
  transports: () => apiList<TransportDto>('/api/deals/transports/'),
  updateProfile: (payload: Record<string, unknown>) => api<CustomerProfileDto>('/api/profiles/me/customer/', {
    method: 'PATCH',
    body: payload,
  }),
  uploadChatAttachment: async (file: File, onProgress?: (percent: number) => void) => {
    const optimized = await optimizeImageForUpload(file);
    const body = new FormData();
    body.append('file', optimized);
    body.append('purpose', 'chat_attachment');
    body.append('visibility', 'deal');
    return uploadFormData<FileAssetDto>('/api/media/', body, onProgress);
  },
  uploadChatPhoto: (file: File) => upload(file, 'checkpoint_media', 'deal'),
  uploadDisputeEvidence: (file: File) => upload(file, 'dispute_evidence', 'deal'),
  uploadReviewMedia: (file: File) => upload(file, 'review_photo', 'deal'),
};
